import { getTrialCost } from "@/Components/UI/Onboarding/Client/trialCost";
import { Client } from "@/types/page";
import { Price } from "@/types/price";
import { PricingVersion } from "@/types/pricingVersion";
import { PromotionCode } from "@/types/PromotionCode";
import classNames from "@/utils/classNames";
import pluralize from "@/utils/plural";
import { ReactNode } from "react";

const TRIAL_REMINDER_DAYS_BEFORE_END = 2;

type TrialTimelinePricingCardProps = {
    price: Price;
    pricingVersion: PricingVersion;
    promotionCode: PromotionCode | null;
    client: Client;
    trialDays: number;
    /** The rate the client used to pay, struck through beside the current one. */
    weeklyPriceBeforeDiscount?: string | null;
};

type TimelineStepProps = {
    testId: string;
    dotClassName: string;
    isLast?: boolean;
    label: string;
    price?: ReactNode;
    children: ReactNode;
};

function TimelineStep({
    testId,
    dotClassName,
    isLast = false,
    label,
    price,
    children,
}: TimelineStepProps) {
    return (
        <li data-testid={testId} className="flex gap-4">
            <div className="flex flex-col items-center">
                <span
                    aria-hidden
                    className={classNames(
                        "mt-1 size-4 shrink-0 rounded-full",
                        dotClassName,
                    )}
                />
                <span
                    aria-hidden
                    className="-mb-1 w-0.5 grow bg-brand-border"
                />
            </div>
            <div className={classNames("grow", isLast ? "" : "pb-5")}>
                <div className="flex items-start justify-between gap-2">
                    <span className="font-bold">{label}</span>
                    {price}
                </div>
                <div className="text-sm text-brand-gray sm:text-base">
                    {children}
                </div>
            </div>
        </li>
    );
}

export function TrialTimelinePricingCard(props: TrialTimelinePricingCardProps) {
    const { baseAmountForDisplay, costAfterPromoForDisplay } =
        getTrialCost(props);
    const reminderInDays = props.trialDays - TRIAL_REMINDER_DAYS_BEFORE_END;

    return (
        <div
            data-testid="pricing-explanation-container"
            className="rounded-lg border border-brand-border p-4 text-brand-black text-md sm:text-lg"
        >
            <div className="mb-3 font-bold">Your plan</div>
            <ol data-testid="pricing-timeline">
                <TimelineStep
                    testId="pricing-timeline-step-today"
                    dotClassName="bg-brand-decoBlue"
                    label="Today"
                    price={
                        <span className="whitespace-nowrap font-bold">
                            {props.promotionCode ? (
                                <>
                                    <span className="font-normal text-brand-gray line-through">
                                        {baseAmountForDisplay}
                                    </span>{" "}
                                    <span className="text-brand-green">
                                        {costAfterPromoForDisplay}
                                    </span>
                                </>
                            ) : (
                                <span className="text-brand-red">
                                    {baseAmountForDisplay}
                                </span>
                            )}
                        </span>
                    }
                >
                    Start your 1 week trial.
                    <br />
                    Includes video session + messaging
                </TimelineStep>
                <TimelineStep
                    testId="pricing-timeline-step-reminder"
                    dotClassName="bg-brand-decoPink"
                    label={`In ${reminderInDays} days`}
                >
                    We&apos;ll email you a reminder. Cancel anytime.
                </TimelineStep>
                <TimelineStep
                    testId="pricing-timeline-step-after-trial"
                    dotClassName="bg-brand-decoOrange"
                    isLast
                    label="After your trial"
                    price={
                        <span className="whitespace-nowrap font-bold">
                            {!!props.weeklyPriceBeforeDiscount && (
                                <>
                                    <span className="font-normal text-brand-gray line-through">
                                        {props.weeklyPriceBeforeDiscount}
                                    </span>{" "}
                                </>
                            )}
                            <span>
                                {props.price.amountPerSessionForDisplay}
                            </span>
                            <span className="text-sm font-normal">/week</span>
                        </span>
                    }
                >
                    Billed as {props.price.amountForDisplay} every{" "}
                    {pluralize(props.price.intervalCount, props.price.interval)}
                </TimelineStep>
            </ol>
        </div>
    );
}
